// Controls whether the Supabase auth cookie survives browser close, based on
// the user's "remember me" choice at login. Without this, @supabase/ssr always
// writes a long-lived (~1 year) cookie regardless of that choice.
import { CookieOptions } from "@supabase/ssr";
import { PARTNER_COOKIE_NAME } from "./partner-cookie";

const SUPABASE_AUTH_COOKIE_PATTERN = /^sb-.*-auth-token(\.\d+)?$/;

// Excludes the portals' isolated cookie: a user can be signed into the dashboard
// and a portal at once, and remember-me/forced-signout handling for one must
// never touch the other's session.
//
// startsWith, not equality: a large session is split into chunks named
// `<cookie>.0`, `<cookie>.1`, which the pattern above matches. An exact compare
// let those chunks through, so a dashboard sign-out or remember-me pass would
// mangle half of a partner session while leaving the unchunked name alone.
export function isSupabaseAuthCookie(name: string): boolean {
  return !name.startsWith(PARTNER_COOKIE_NAME) && SUPABASE_AUTH_COOKIE_PATTERN.test(name);
}

// Strips maxAge/expires from Supabase auth cookies when the session should
// not outlive the browser. Non-auth cookies and remembered sessions pass
// through unchanged. `value` matters: sign-out clears cookies by writing an
// empty value with an expired maxAge/expires — stripping those would turn a
// deletion into "session cookie holding an empty string" instead of an
// immediate delete, so empty-value writes always pass through untouched.
export function applyRememberMePersistence(
  name: string,
  value: string,
  options: CookieOptions | undefined,
  isRemembered: boolean
): CookieOptions | undefined {
  if (isRemembered || !value || !isSupabaseAuthCookie(name) || !options) {
    return options;
  }

  const { maxAge, expires, ...sessionOnlyOptions } = options;
  return sessionOnlyOptions;
}
